import type { ComponentType } from 'react';
import type { AllowlistRole } from '@bbbk/schema';
import { TodayPage } from './TodayPage';
import { BookingsPage } from './BookingsPage';
import { DogsOwnersPage } from './DogsOwnersPage';
import { RotaPage } from './RotaPage';
import { AccountPage } from './AccountPage';
import { FinancesPage } from './FinancesPage';
import { MessagesPage } from './MessagesPage';
import { ComingSoonPage } from './ComingSoonPage';

// The route map — the single source of which component renders for the active
// nav key (AC1). Adding or changing a section is a one-line edit here, not
// another `view === '…'` branch in AppShell. `component` is omitted for
// sections that are not yet built; those resolve to the ComingSoon placeholder.

export type NavItem = {
  key: string;
  label: string;
  icon: string;
  // The URL path this section lives at (bookmarkable, deep-linkable). The nav
  // key stays the internal identifier; the path is what the browser shows.
  path: string;
  component?: ComponentType;
  // Opt out of keep-alive per route (keep-alive-page-host AC5). When true, the
  // page host unmounts this section on leave and remounts it fresh on return
  // (re-fetching from empty), exactly as a non-kept page did before this story.
  // Absent / false ⇒ keep-alive + revalidate-on-return (the default). The
  // opt-out is declared here, never inside the page.
  remountOnReturn?: boolean;
  // Show this nav item only to admins. The route still resolves for anyone who
  // deep-links to it — the API is the real gate (403 for non-admins).
  adminOnly?: boolean;
  // Restrict the nav item to specific roles (e.g. finances → admin + owner, not
  // helpers). The API is still the real gate; this only hides the nav entry.
  roles?: Array<'admin' | 'owner' | 'helper'>;
};

export type NavGroup = { group: string; items: NavItem[] };

// Sidebar structure + route targets, carried verbatim from the prototype
// (app.jsx NAV_ITEMS). Order and labels are the visual contract — unchanged.
export const NAV: NavGroup[] = [
  {
    group: 'Operations',
    items: [
      { key: 'today', label: 'Daily', icon: 'home', path: '/today', component: TodayPage },
      { key: 'bookings', label: 'Bookings', icon: 'calendar', path: '/bookings', component: BookingsPage },
      { key: 'dogs', label: 'Dogs & Owners', icon: 'paw', path: '/dogs', component: DogsOwnersPage },
      { key: 'schedule', label: 'Rota', icon: 'clock', path: '/rota', component: RotaPage },
    ],
  },
  {
    group: 'Business',
    items: [
      { key: 'finances', label: 'Finances', icon: 'bar', path: '/finances', component: FinancesPage, roles: ['admin', 'owner'] },
      { key: 'messages', label: 'Messages', icon: 'message', path: '/messages', component: MessagesPage },
    ],
  },
  {
    // Admin-only section — the whole group is hidden for non-admins (AppShell
    // drops a group whose visible items all filter out).
    group: 'Admin',
    items: [
      { key: 'account', label: 'Account', icon: 'user', path: '/account', component: AccountPage, adminOnly: true },
    ],
  },
];

// The view AppShell lands on at first mount. Today (the daily dashboard) is the
// landing view now that it's built (daily-dashboard AC9); Bookings is one nav
// click away.
export const DEFAULT_VIEW = 'today';

// The nav-visibility role: the FULL allowlist role set (single-sourced in
// @bbbk/schema — `customer` included since portal-foundations) plus a null
// "unknown / not-yet-resolved" state the shell tolerates before /me lands —
// visibleNav returns [] for it.
export type Role = AllowlistRole | null;

// The sidebar a given role sees. Admin-only items are dropped for non-admins,
// and any group left with zero visible items is dropped WHOLE — heading and
// all — so a non-admin never sees an empty "Admin" label. Single source of nav
// visibility; AppShell renders exactly what this returns.
export function visibleNav(role: Role): NavGroup[] {
  // Neither of these two roles reaches the admin shell: App.tsx routes `staff`
  // to the self-view, and a `customer` is refused by every API gate on the desk
  // (no requireRoles call names the word). But if one somehow did, they get NO
  // nav — because the filter below is an ALLOW-list only for items that declare
  // `roles`, and an unknown role would otherwise fall through to every item
  // that declares none. Defence in depth, and the reason this is a list rather
  // than a single comparison.
  if (role === 'staff' || role === 'customer') return [];
  return NAV.map((g) => ({
    ...g,
    items: g.items.filter(
      (it) =>
        (!it.adminOnly || role === 'admin') &&
        (!it.roles || (role !== null && it.roles.includes(role))),
    ),
  })).filter((g) => g.items.length > 0);
}

const ITEMS: NavItem[] = NAV.flatMap((g) => g.items);

export const NAV_LABELS: Record<string, string> = Object.fromEntries(
  ITEMS.map((it) => [it.key, it.label]),
);

const BY_KEY: Record<string, NavItem> = Object.fromEntries(ITEMS.map((it) => [it.key, it]));

// The path AppShell lands on at first mount / for an unknown route.
export const DEFAULT_PATH = '/today';
const PATH_BY_VIEW: Record<string, string> = Object.fromEntries(ITEMS.map((it) => [it.key, it.path]));
const VIEW_BY_PATH: Record<string, string> = Object.fromEntries(ITEMS.map((it) => [it.path, it.key]));

// view key → URL path (falls back to the default path for an unknown key).
export function viewToPath(view: string): string {
  return PATH_BY_VIEW[view] ?? DEFAULT_PATH;
}
// URL path → view key. Unknown paths (including '/') resolve to the default view.
export function pathToView(pathname: string): string {
  return VIEW_BY_PATH[pathname] ?? DEFAULT_VIEW;
}

// Look up the NavItem for a key (undefined for an unknown key). Exported so the
// page host can read the per-route keep-alive flag.
export function navItem(view: string): NavItem | undefined {
  return BY_KEY[view];
}

// Whether a section is kept alive across nav (keep-alive-page-host AC2/AC5).
// Default — flag absent, or an unknown key — is keep-alive; a route is excluded
// only by declaring `remountOnReturn: true`.
export function isKeepAlive(view: string): boolean {
  return BY_KEY[view]?.remountOnReturn !== true;
}

export type ResolvedSection = {
  title: string;
  Component: ComponentType;
  placeholder: boolean;
};

// Resolve a nav key to the page that renders for it. A built key returns its
// component and label. An unbuilt key (no `component`) returns the ComingSoon
// placeholder carrying its nav label. An unknown key falls back to the key
// itself as the title — preserving the former `LABELS[view] ?? view` behaviour.
export function resolveSection(view: string): ResolvedSection {
  const item = BY_KEY[view];
  if (item?.component) {
    return { title: item.label, Component: item.component, placeholder: false };
  }
  const title = item?.label ?? view;
  return {
    title,
    Component: () => ComingSoonPage({ label: title }),
    placeholder: true,
  };
}
